import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { notificationsAPI, type Notification } from '@/api/notifications';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  useMutationWithBroadcast,
  useBroadcastSubscription,
} from './use-mutation-with-broadcast';

// NOTE: Notifications need cross-device sync so marking as read on mobile reflects on web

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  markAsRead: (notificationIds: string[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  loadMore: () => void;
  hasMore: boolean;
  refetch: () => void;
}

const NOTIFICATIONS_PER_PAGE = 20;

export function useNotifications(): UseNotificationsReturn {
  const [page, setPage] = useState(1);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const queryClient = useQueryClient();
  const location = useLocation();
  const { user } = useAuth();
  
  // Detect current role based on path (same logic as ChatPopupManager)
  const getCurrentRole = (): 'client' | 'filmmaker' | 'admin' | null => {
    const path = location.pathname;
    if (path.includes('/client/')) return 'client';
    if (path.includes('/admin/')) return 'admin';
    if (path.includes('/filmmaker/') || path.includes('/dashboard')) return 'filmmaker';
    return null;
  };
  
  const currentRole = getCurrentRole();

  // Fetch notifications
  const {
    data: notificationsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['notifications', page, currentRole],
    queryFn: async () => {
      const response = await notificationsAPI.getNotifications(page, NOTIFICATIONS_PER_PAGE, currentRole);
      return response;
    },
    staleTime: 1000 * 60, // 1 minute
    enabled: !!currentRole, // Only fetch when we have a role
  });

  // Update all notifications when new page loads
  useEffect(() => {
    if (notificationsResponse?.data) {
      if (page === 1) {
        setAllNotifications(notificationsResponse.data);
      } else {
        setAllNotifications(prev => [...prev, ...notificationsResponse.data]);
      }
      setHasMore(notificationsResponse.hasMore);
    }
  }, [notificationsResponse, page]);

  // Fetch unread count — no polling; refetchOnWindowFocus handles catch-up,
  // broadcast subscription handles instant sync on mutations
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count', currentRole],
    queryFn: async () => {
      return await notificationsAPI.getUnreadCount(currentRole);
    },
    staleTime: 1000 * 60 * 2, // consider fresh for 2 min; refetch on next window focus after that
    enabled: !!currentRole,
  });

  // Subscribe to user-specific notification channel for cross-device sync
  useBroadcastSubscription(
    user?.id ? [{
      channel: `notifications:${user.id}`,
      event: 'notification_change',
      onReceive: (payload) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NotificationsBroadcast] Received notification_change', payload);
        }
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      },
    }] : [],
    [user?.id, queryClient]
  );

  // Mark as read mutation with broadcast
  const markAsReadMutation = useMutationWithBroadcast({
    mutationFn: async (notificationIds: string[]) => {
      await notificationsAPI.markMultipleAsRead(notificationIds);
      return notificationIds;
    },
    invalidateKeys: [
      ['notifications'],
      ['notifications-unread-count'],
    ],
    broadcast: user?.id ? {
      channel: `notifications:${user.id}`,
      event: 'notification_change',
      getPayload: (_, notificationIds) => ({
        changeType: 'marked_read',
        notification_ids: notificationIds,
      }),
    } : undefined,
    onError: (error) => {
      toast.error('Failed to mark notifications as read');
      console.error('Mark as read error:', error);
    },
  });

  // Mark all as read mutation with broadcast
  const markAllAsReadMutation = useMutationWithBroadcast({
    mutationFn: async () => {
      await notificationsAPI.markAllAsRead();
    },
    invalidateKeys: [
      ['notifications'],
      ['notifications-unread-count'],
    ],
    broadcast: user?.id ? {
      channel: `notifications:${user.id}`,
      event: 'notification_change',
      getPayload: () => ({
        changeType: 'marked_all_read',
      }),
    } : undefined,
    onSuccess: () => {
      toast.success('All notifications marked as read');
    },
    onError: (error) => {
      toast.error('Failed to mark all notifications as read');
      console.error('Mark all as read error:', error);
    },
  });

  // Delete notification mutation with broadcast
  const deleteNotificationMutation = useMutationWithBroadcast({
    mutationFn: async (notificationId: string) => {
      await notificationsAPI.deleteNotification(notificationId);
      return notificationId;
    },
    invalidateKeys: [
      ['notifications'],
      ['notifications-unread-count'],
    ],
    broadcast: user?.id ? {
      channel: `notifications:${user.id}`,
      event: 'notification_change',
      getPayload: (_, notificationId) => ({
        changeType: 'deleted',
        notification_id: notificationId,
      }),
    } : undefined,
    onError: (error) => {
      toast.error('Failed to delete notification');
      console.error('Delete notification error:', error);
    },
  });

  const loadMore = useCallback(() => {
    if (hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [hasMore]);

  return {
    notifications: allNotifications,
    unreadCount,
    isLoading,
    error: error as Error | null,
    markAsRead: markAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutateAsync,
    deleteNotification: deleteNotificationMutation.mutateAsync,
    loadMore,
    hasMore,
    refetch: () => {
      setPage(1);
      refetch();
    },
  };
}

// Hook for just the unread count (for use in header badge)
export function useNotificationCount(): number {
  const location = useLocation();
  
  // Detect current role based on path
  const getCurrentRole = (): 'client' | 'filmmaker' | 'admin' | null => {
    const path = location.pathname;
    if (path.includes('/client/')) return 'client';
    if (path.includes('/admin/')) return 'admin';
    if (path.includes('/filmmaker/') || path.includes('/dashboard')) return 'filmmaker';
    return null;
  };
  
  const currentRole = getCurrentRole();
  
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count', currentRole],
    queryFn: async () => {
      return await notificationsAPI.getUnreadCount(currentRole);
    },
    staleTime: 1000 * 60 * 2,
    enabled: !!currentRole,
  });

  return unreadCount;
}